import React from 'react';
import { Box, Typography, Container, Grid, Button } from '@mui/material';
import { motion } from 'framer-motion';
import { useTheme } from '../../../ThemeContext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const IndustryPageTemplate = ({ title, subtitle, desc, image, themeColor, bgGradient, features }) => {
  const { isDarkMode } = useTheme();

  return (
    <Box sx={{ pt: { xs: 12, md: 16 }, pb: 8, background: isDarkMode ? '#030712' : '#f8fafc', minHeight: '100vh' }}>
      
      {/* Hero Section */}
      <Container maxWidth="xl" sx={{ mb: 8 }}>
        <Box
          sx={{
            position: 'relative',
            background: isDarkMode ? 'rgba(255,255,255,0.02)' : '#ffffff',
            borderRadius: '32px',
            overflow: 'hidden',
            border: isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.04)',
            boxShadow: isDarkMode ? '0 24px 48px rgba(0,0,0,0.4)' : '0 24px 48px rgba(0,0,0,0.05)',
            display: 'flex',
            flexDirection: { xs: 'column-reverse', lg: 'row' },
            alignItems: 'center',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0, left: 0, right: '50%', bottom: 0,
              background: bgGradient,
              zIndex: 0,
            }
          }}
        >
          {/* Text Content */}
          <Box sx={{ p: { xs: 4, md: 8 }, flex: 1, position: 'relative', zIndex: 2 }}>
            <Typography variant="h2" fontWeight={900} sx={{ color: isDarkMode ? '#fff' : '#0f172a', mb: 2, fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="h5" sx={{ color: themeColor, fontWeight: 700, mb: 3 }}>
                {subtitle}
              </Typography>
            )}
            <Typography sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '1.1rem', lineHeight: 1.8, mb: 5, maxWidth: '600px' }}>
              {desc}
            </Typography>
            <Button
              variant="contained"
              size="large"
              href="https://play.google.com/store/apps/details?id=com.filmhookapps"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                background: themeColor,
                color: '#fff',
                borderRadius: '30px',
                px: 4, py: 1.5,
                fontWeight: 800,
                textTransform: 'none',
                fontSize: '1rem',
                boxShadow: `0 8px 24px ${themeColor}60`,
                '&:hover': { background: themeColor, filter: 'brightness(1.1)' }
              }}
            >
              Get Started with {title}
            </Button>
          </Box>

          {/* Image */}
          <Box sx={{ flex: 1, p: 4, display: 'flex', justifyContent: 'center', position: 'relative', zIndex: 2 }}>
            <Box
              component="img"
              src={image}
              alt={title}
              sx={{
                width: '100%',
                maxWidth: '600px',
                height: 'auto',
                objectFit: 'contain',
                filter: isDarkMode ? 'drop-shadow(0 0 40px rgba(0,0,0,0.5))' : 'drop-shadow(0 20px 40px rgba(0,0,0,0.1))'
              }}
            />
          </Box>
        </Box>
      </Container>

      {/* Features Grid */}
      <Container maxWidth="xl">
        <Typography variant="h4" fontWeight={800} textAlign="center" sx={{ color: isDarkMode ? '#fff' : '#0f172a', mb: 6 }}>
          Everything you need for <Box component="span" sx={{ color: themeColor }}>{title}</Box>
        </Typography>
        
        <Grid container spacing={4}>
          {features.map((feat, idx) => (
            <Grid item xs={12} sm={6} md={3} key={idx}>
              <Box
                component={motion.div}
                whileHover={{ translateY: -8 }}
                sx={{
                  background: isDarkMode ? 'rgba(255,255,255,0.03)' : '#ffffff',
                  p: 4,
                  borderRadius: '24px',
                  height: '100%',
                  border: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.04)',
                  boxShadow: isDarkMode ? 'none' : '0 10px 30px rgba(0,0,0,0.03)',
                }}
              >
                <CheckCircleIcon sx={{ color: themeColor, fontSize: '2.5rem', mb: 2 }} />
                <Typography variant="h6" fontWeight={800} sx={{ color: isDarkMode ? '#fff' : '#0f172a', mb: 1.5 }}>
                  {feat.title}
                </Typography>
                <Typography sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '0.9rem', lineHeight: 1.7 }}>
                  {feat.desc}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

    </Box>
  );
};

export default IndustryPageTemplate;
